import { type ApiRoutes } from "@terralux/api";
import { API_VERSION } from "@terralux/api/version";
import { ApiError } from "@terralux/common/apiError";
import { hc } from "hono/client";
import { CORRELATION_ID } from "./constants";

export const API_STATUS_BAD_REQUEST = 400;
export const API_STATUS_UNAUTHORIZED = 401;
export const API_STATUS_FORBIDDEN = 403;
export const API_STATUS_PRECONDITION_FAILED = 412;
export const API_STATUS_CONTENT_TOO_LARGE = 413;

export function createApiClient({
  throwOnErrorResponse,
  onError,
}: {
  throwOnErrorResponse?: boolean;
  onError?: (options: { type: "error"; error: Error | unknown } | { type: "response"; response: Response }) => void;
}) {
  // Fetch wrapper so we can handle errors in a unified way.
  // Ideally Hono client would have an onError callback, but it does not.
  async function customFetch(...params: Parameters<typeof fetch>): ReturnType<typeof fetch> {
    let response;
    try {
      response = await fetch(...params);
    } catch (error) {
      if (onError) {
        onError({ type: "error", error });
      }

      throw new ApiError({
        message: "Fetch failed",
        cause: error,
      });
    }

    if (response.ok === false) {
      if (onError) {
        onError({ type: "response", response });
      }

      if (throwOnErrorResponse === true) {
        if (response.status === API_STATUS_PRECONDITION_FAILED) {
          throw new ApiError({
            message: "API status precondition failed error from API",
            status: response.status,
          });
        } else {
          throw new ApiError({
            message: "Response not successful",
            status: response.status,
          });
        }
      }
    }

    return response;
  }

  // For $url() to work, the API URL can't be a relative URL.
  // See https://hono.dev/docs/guides/rpc#url
  const absoluteApiUrlObject = new URL("/api", window.location.href);

  return hc<ApiRoutes>(absoluteApiUrlObject.href, {
    fetch: customFetch,
    init: {
      credentials: "include",
    },
    headers: {
      "X-Correlation-ID": CORRELATION_ID,
      "X-Expected-API-Version": API_VERSION,
    },
  });
}

export const routeLoaderApiClient = createApiClient({ throwOnErrorResponse: true });
